"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Droplets, Shield, Sparkles, Wind, CircleCheck } from "lucide-react";
const steps = [{
  id: "01",
  title: "Anfrage",
  description: "Kontaktieren Sie uns mit Ihren Event-Details. Wir beraten Sie zur optimalen Bechermenge und den passenden Modellen für Ihre Veranstaltung.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/2.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/2.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/2.png-wvc-sizes",
  icon: Wind
}, {
  id: "02",
  title: "Planung",
  description: "Wir erstellen ein maßgeschneidertes Logistikkonzept inklusive Lieferterminen, Reinigungsintervallen und optionalem individuellem Druck.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/new_step2.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/new_step2.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/new_step2.png-wvc-sizes",
  icon: Sparkles,
  imageClassName: "absolute inset-0 bg-gradient-to-t from-background/40 to-transparent z-10 !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/2.png)]"
}, {
  id: "03",
  title: "Lieferung",
  description: "Pünktliche Zustellung der hygienisch verpackten Mehrwegbecher direkt zu Ihrem Veranstaltungsort in ganz Kärnten durch unsere Logistik-Partner.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/new_step3.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/new_step3.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/new_step3.png-wvc-sizes",
  icon: Shield
}, {
  id: "04",
  title: "Event",
  description: "Ihre Gäste genießen Getränke aus hochwertigen Bechern, während Sie aktiv Müll vermeiden und ein Zeichen für Nachhaltigkeit setzen.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/ChatGPT Image 4. Mai 2026, 08_47_35.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/ChatGPT Image 4. Mai 2026, 08_47_35.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/ChatGPT Image 4. Mai 2026, 08_47_35.png-wvc-sizes",
  icon: Droplets
}, {
  id: "05",
  title: "Rückgabe & Reinigung",
  description: "Nach dem Event holen wir die Becher ab. In unserer modernen Industrie-Waschanlage werden sie professionell gereinigt und getrocknet.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/6.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/6.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/6.png-wvc-sizes",
  icon: CircleCheck
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="224836" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-16 md:mb-24 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute left-8 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center z-20">
        <div className={`w-6 h-6 rounded-full border-4 border-background flex items-center justify-center transition-colors duration-700 delay-300 ${isVisible ? "bg-primary shadow-lg" : "bg-muted"}`}>
          <div className={`w-2 h-2 rounded-full ${isVisible ? "bg-background" : "bg-transparent"}`} />
        </div>
      </div>

      {}
      <div className={`absolute left-8 top-6 bottom-[-4rem] md:hidden w-1 -translate-x-1/2 transition-all duration-1000 origin-top ${isVisible ? "bg-primary scale-y-100" : "bg-border scale-y-0"}`} />

      {}
      <div className={`w-full pl-20 md:pl-0 md:w-[45%] flex ${isEven ? "md:justify-end md:pr-12" : "md:justify-start md:pl-12 md:order-last"}`}>
        <Card className="relative overflow-hidden bg-card/60 backdrop-blur-xl border-border/50 shadow-xl group hover:border-primary/50 transition-colors duration-500 w-full rounded-xl">
          {}
          <div className="absolute -right-4 -top-8 text-[8rem] font-default font-black text-primary/5 select-none pointer-events-none transition-transform duration-700 group-hover:scale-110">
            {step.id}
          </div>

          <CardContent className="p-6 md:p-8 relative z-10 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-primary/10 text-primary">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-default font-bold text-foreground">
                {step.title}
              </h3>
            </div>

            <p className="text-muted-foreground font-default leading-relaxed">
              {step.description}
            </p>

            <div className={`relative w-full rounded-lg overflow-hidden mt-2 ${index === 3 || index === 4 ? "aspect-video" : "h-48"}`}>
              <div className={`${step.imageClassName || ""} absolute inset-0 bg-gradient-to-t from-background/40 to-transparent z-10`} />
              <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.title} loading="lazy" decoding="async" className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${index === 3 || index === 4 ? "object-contain" : "object-cover"}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:block md:w-[45%]" />
    </div>;
}
export default function TheEcoShineProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="der-cup4us-kreislauf" className="py-24 md:py-32 bg-background relative overflow-hidden dark">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Der <span className="text-primary">Cup4us</span> Kreislauf
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Unser effizienter 5-Schritte-Prozess garantiert Ihnen maximale Nachhaltigkeit bei minimalem Aufwand – von der ersten Anfrage bis zur hygienischen Reinigung.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2" />

          {}
          <div className="relative flex flex-col">
            {steps.map((step, index) => <TimelineStep key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}